import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { IconButton } from "@material-ui/core";
import Modal from "./modal";
import Button from "./button";
import Labelbox from "./labelbox";
import Table from "./table";
import { AddCircleOutlineOutlined } from "@material-ui/icons";
import CloseIcon from "@material-ui/icons/Close";
import { useDispatch, useSelector } from "react-redux";
import {
  CLIENT_ACTION,
  EMPLOYEE_ACTION,
  MASTER_ACTION,
  TIMESHEET_ACTION,
} from "../redux/action";
import {
  Toaster,
  dropdownName,
  normalizeTimeHHmm,
} from "../utils/common";
import {
  getActivityCanonicalId,
  normalizeActivityOption,
  normalizeBilledType,
  normalizeSubActivityOption,
  isSubActivityTextMode,
  resolveSubActivityEditValue,
  sortTimesheetItemsByDateAsc,
  isLeaveBilledType,
  isTimesheetTotalBelowMinimum,
  timesheetMinimumHoursForHolidays,
} from "../utils/timesheetHelpers";
import { isOthersActivityOption } from "../utils/timesheetExcelTemplate";
import moment from "moment";
import {
  formatDateOnlyYmd,
} from "../utils/dateOnly";
import "../pages/timesheet/timesheet.scss";

const EditTimesheetModal = ({ open, handleClose, timesheetId }) => {
  const dispatch = useDispatch();
  const {
    client: { activeClients, allClients },
    master: { activity },
  } = useSelector((state) => state);

  const submitValidateRef = useRef();
  const statusMode = useRef({});
  const [holidayDates, setHolidayDates] = useState([]);
  const holidayDateSet = useMemo(
    () => new Set(holidayDates.map((holiday) => holiday.date)),
    [holidayDates],
  );
  const requiredTimesheetHours = timesheetMinimumHoursForHolidays(
    holidayDates.length,
  );
  const [clientInputModeByRow, setClientInputModeByRow] = useState([]);
  const [subActivityOptionsByRow, setSubActivityOptionsByRow] = useState([]);

  const [fieldConfiguration, setFieldConfiguration] = useState({
    start_date: {
      value: "",
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
      minDate: null,
      maxDate: null,
      customDateInput: "",
    },
    end_date: {
      value: "",
    },
    total_hours: {
      value: "00:00",
    },
    rejected_comments: {
      value: "",
    },
    itemDetails: [],
  });

  const headers = [
    { id: "1", label: "Date" },
    { id: "2", label: "Billed Type" },
    { id: "3", label: "Client" },
    { id: "4", label: "Business Name" },
    { id: "5", label: "Activity" },
    { id: "6", label: "Sub Activity" },
    { id: "7", label: "Description" },
    { id: "8", label: "Hours" },
  ];

  const billedTypeOptions = [
    { id: "Billable", value: "Billable" },
    { id: "Non-billable", value: "Non-billable" },
    { id: "Leave", value: "Leave" },
  ];

  const loadTimesheetData = useCallback(async () => {
    if (!timesheetId) return;

    try {
      const timesheetEntries = await TIMESHEET_ACTION.getTimesheetEntries({
        timesheet_id: timesheetId,
      });

      console.log("API Response - timesheetEntries:", timesheetEntries);

      if (timesheetEntries && timesheetEntries.length > 0) {
        const data = timesheetEntries[0];
        console.log("API Response - data:", data);
        console.log("API Response - itemDetails:", data.itemDetails);
        // const startDate = moment(data.start_date).format("YYYY-MM-DD");
        // const endDate = moment(data.end_date).format("YYYY-MM-DD");
        const startDate = formatDateOnlyYmd(data.start_date);
        const endDate =
          formatDateOnlyYmd(data.end_date) || startDate;
        const holidayRows = await TIMESHEET_ACTION.getTimesheetHolidays({
          start_date: startDate,
          end_date: endDate,
          employee_id: data.employee_id,
        });
        setHolidayDates(Array.isArray(holidayRows) ? holidayRows : []);
        // Update date configuration
        setFieldConfiguration((prev) => ({
          ...prev,
          start_date: {
            ...prev.start_date,
            value: startDate,
            minDate: startDate,
            maxDate: endDate,
            customDateInput: `${moment(startDate, "YYYY-MM-DD").format(
              "MMM DD yy",
            )} - ${moment(endDate, "YYYY-MM-DD").format("MMM DD yy")}`,
          },
          end_date: {
            value: endDate,
          },
          rejected_comments: {
            value: data.rejected_comments || "",
          },
        }));

        statusMode.current = {
          timesheet_id: data.timesheet_id,
          currentData: data,
          currentDataItemRemoved: [],
          status: data.status,
        };

        // Load and map itemDetails
        if (data.itemDetails && data.itemDetails.length > 0) {
          // Get unique activity IDs
          const uniqueActivityIds = [
            ...new Set(
              data.itemDetails.map((item) => item.activity_id).filter(Boolean),
            ),
          ];

          // Load subactivities for each unique activity_id
          const subActivityMap = {};
          for (const activityId of uniqueActivityIds) {
            const subActivities =
              await MASTER_ACTION.getSubActivity(activityId);
            if (Array.isArray(subActivities)) {
              const normalized = subActivities.map(normalizeSubActivityOption);
              subActivityMap[activityId] = normalized;
              subActivityMap[String(activityId)] = normalized;
            }
          }

          const itemList = sortTimesheetItemsByDateAsc(data.itemDetails).map(
            (entry, index) => {
              const actId = entry.activity_id;
              const rowSubActivities =
                subActivityMap[actId] || subActivityMap[String(actId)] || [];

              // Format date as YYYY-MM-DD string (same as create-timesheet page)
              // This is what the table + Labelbox datepicker expect
              let dateString = "";
              if (entry.date) {
                dateString =
                  formatDateOnlyYmd(entry.date) ||
                  moment(entry.date).format("YYYY-MM-DD");
              }

              // Check if this is an "Others" entry (activity_id is NULL and custom_sub_activity exists)
              const isOthers =
                (entry.activity_id === null || entry.activity_id === 0) &&
                entry.custom_sub_activity;

              const canonicalActivityId =
                !isOthers && actId
                  ? String(getActivityCanonicalId({ activity_id: actId, id: actId }))
                  : "";

              const hasClientId = !!entry.client_id;
              const clientEmail =
                entry.client_email ||
                entry.email ||
                entry.clientEmail ||
                entry.client_email_id ||
                "";
              const useEmail = !hasClientId && !!clientEmail;

              return {
                // Column 0: Date (string, e.g. "2025-12-03")
                date: dateString,
                // Column 1: Billed Type
                billed_type: entry.billed_type || "",
                // Column 2: Client
                client_id: useEmail ? String(clientEmail) : entry.client_id || "",
                // Column 3: Business Name
                business_name: entry.business_name || "",
                // Column 4: Activity
                activity_id: isOthers
                  ? ""
                  : canonicalActivityId,
                sub_activity_id: resolveSubActivityEditValue(
                  entry,
                  rowSubActivities,
                ),
                description: entry.description || "",
                hours: normalizeTimeHHmm(entry.hours) || entry.hours || "",
                // Hidden fields
                timesheet_item_id: entry.timesheet_item_id,
                timesheet_id: data.timesheet_id || entry.timesheet_id || null, // Include parent timesheet_id
                status: entry.status,
                custom_sub_activity: entry.custom_sub_activity || "",
                __clientTextMode: useEmail,
              };
            });

          setFieldConfiguration((prev) => ({
            ...prev,
            itemDetails: [...itemList],
          }));
          setClientInputModeByRow(itemList.map((row) => !!row?.__clientTextMode));
          setSubActivityOptionsByRow(
            itemList.map((row) => {
              if (!row.activity_id) return [];
              return (
                subActivityMap[row.activity_id] ||
                subActivityMap[String(row.activity_id)] ||
                []
              );
            }),
          );
        }
      }
    } catch (error) {
      console.error("Error loading timesheet data:", error);
      Toaster.error("Error loading timesheet data");
    }
  }, [timesheetId]);

  // Load employees and clients when modal opens
  useEffect(() => {
    if (open && timesheetId) {
      dispatch(EMPLOYEE_ACTION.getActiveEmployees());
      dispatch(CLIENT_ACTION.getActiveClients({ scope: "all" }));
      loadTimesheetData();
    }
  }, [open, timesheetId, dispatch, loadTimesheetData]);

  useEffect(() => {
    if (!open) return;
    const role = (localStorage.getItem("user_role") || "").toLowerCase();
    if (role !== "employee" && role !== "manager") return;
    if (activeClients?.length > 0) return;
    dispatch(CLIENT_ACTION.getAllClients({ scope: "all" }));
  }, [open, dispatch, activeClients?.length]);

  const timesheetClientList = useMemo(() => {
    const primary = activeClients?.length ? activeClients : allClients || [];
    // Timesheet client dropdown must show ALL clients (not just un-activated).
    // Backend returns Activated=1 for active clients, so filtering this hides everything.
    return primary;
  }, [activeClients, allClients]);

  const clientsData = useMemo(() => {
    const list = (timesheetClientList || []).map((data) => {
      const { client_id, email } = data;
      return {
        id: client_id,
        value: dropdownName(data) + " - " + email,
      };
    });
    return [
      { id: "", value: "Clear selection", keepAtTop: true },
      { id: "Others", value: "Others", keepAtTop: true },
      ...list,
    ];
  }, [timesheetClientList]);

  const activityOptions = useMemo(() => {
    if (!activity || !Array.isArray(activity)) return [];
    return activity
      .map(normalizeActivityOption)
      .filter((a) => !isOthersActivityOption(a));
  }, [activity]);

  const filterActivitiesByBilledType = React.useCallback(
    (billedType) => {
      if (!Array.isArray(activityOptions)) return [];
      const target = normalizeBilledType(billedType);
      if (!target) return activityOptions;
      if (target === "leave") return [];
      const anyHasBillingType = activityOptions.some((a) =>
        normalizeBilledType(
          a?.billing_type || a?.billed_type || a?.billingType,
        ),
      );
      if (!anyHasBillingType) return activityOptions;
      const filtered = activityOptions.filter((a) => {
        const bt = normalizeBilledType(
          a?.billing_type || a?.billed_type || a?.billingType,
        );
        return bt === target;
      });
      return filtered;
    },
    [activityOptions],
  );

  const activityDependentDropdown = useMemo(() => {
    return (fieldConfiguration.itemDetails || []).map((row) =>
      filterActivitiesByBilledType(row?.billed_type),
    );
  }, [
    fieldConfiguration.itemDetails,
    filterActivitiesByBilledType,
  ]);

  const calculation = (mutated_time, rowIndex) => {
    if (
      !fieldConfiguration?.itemDetails ||
      fieldConfiguration.itemDetails.length === 0
    ) {
      return "00:00";
    }
    let totalSeconds = fieldConfiguration.itemDetails.reduce(
      (accumulator, currentValue, index) => {
        const time =
          index === rowIndex && mutated_time
            ? mutated_time
            : currentValue.hours;
        if (!time) {
          return accumulator;
        }
        const currentDuration = time.split(":");
        const hrs = parseInt(currentDuration[0], 10) || 0;
        const min = parseInt(currentDuration[1], 10) || 0;
        const currDurationSec = 60 * min + 60 * 60 * hrs;
        return (accumulator += currDurationSec);
      },
      0,
    );
    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    const minutes = Math.floor(totalSeconds / 60);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}`;
  };

  const onChange = async (value, key, rowIndex, inlineCallback) => {
    if (key === "hours" && (rowIndex || rowIndex === 0)) {
      const fieldValue = normalizeTimeHHmm(value);
      setFieldConfiguration((prev) => {
        const newItemDetails = [...prev.itemDetails];
        newItemDetails[rowIndex][key] = fieldValue;
        const totalHours = calculation(fieldValue, rowIndex);
        return {
          ...prev,
          itemDetails: newItemDetails,
          total_hours: { ...prev.total_hours, value: totalHours },
        };
      });
    } else if (key === "client_id" && (rowIndex || rowIndex === 0)) {
      setFieldConfiguration((prev) => {
        const newItemDetails = [...prev.itemDetails];
        const currentRow = newItemDetails[rowIndex] || {};
        const selectedClientOption = (clientsData || []).find(
          (option) => String(option.id) === String(value),
        );
        const isOthersSelection =
          value === "Others" || selectedClientOption?.value === "Others";
        const isClientTextMode = !!clientInputModeByRow[rowIndex];

        if (isOthersSelection) {
          currentRow.client_id = "";
          currentRow.business_name = "";
          setClientInputModeByRow((prev) => {
            const next = [...prev];
            next[rowIndex] = true;
            return next;
          });
        } else if (isClientTextMode) {
          currentRow.client_id = value;
        } else {
          const clientData = value
            ? timesheetClientList.find((data) => data.client_id === value)
            : null;
          currentRow.client_id = value;
          currentRow.business_name =
            clientData instanceof Object ? clientData.business_name : "";
        }
        newItemDetails[rowIndex] = currentRow;
        return {
          ...prev,
          itemDetails: newItemDetails,
        };
      });
    } else if (key === "activity_id" && (rowIndex || rowIndex === 0)) {
      const activityId = Number(value);
      const hasValidId = !Number.isNaN(activityId) && activityId > 0;

      if (hasValidId) {
        const subActivities = await MASTER_ACTION.getSubActivity(activityId);
        if (Array.isArray(subActivities)) {
          setSubActivityOptionsByRow((prev) => {
            const next = [...prev];
            next[rowIndex] = subActivities.map(normalizeSubActivityOption);
            return next;
          });
        }
      } else {
        setSubActivityOptionsByRow((prev) => {
          const next = [...prev];
          next[rowIndex] = [];
          return next;
        });
      }

      setFieldConfiguration((prev) => {
        const newItemDetails = [...prev.itemDetails];
        newItemDetails[rowIndex][key] = hasValidId ? String(value) : "";
        newItemDetails[rowIndex].sub_activity_id = "";
        if (!hasValidId) newItemDetails[rowIndex].custom_sub_activity = "";
        return {
          ...prev,
          itemDetails: newItemDetails,
        };
      });
    } else if (key === "date" && (rowIndex || rowIndex === 0)) {
      const formattedDate =
        formatDateOnlyYmd(value) ||
        (value ? moment(value).format("YYYY-MM-DD") : "");
      if (holidayDateSet.has(formattedDate)) {
        Toaster.error("Timesheet entries cannot be added on a holiday");
        return;
      }

      setFieldConfiguration((prev) => {
        const newItemDetails = [...prev.itemDetails];
        newItemDetails[rowIndex][key] = formattedDate; // ✅ ALWAYS STRING
        return {
          ...prev,
          itemDetails: newItemDetails,
        };
      });
    } else if (key === "billed_type" && (rowIndex || rowIndex === 0)) {
      setFieldConfiguration((prev) => {
        const newItemDetails = [...prev.itemDetails];
        const currentRow = newItemDetails[rowIndex] || {};
        currentRow.billed_type = value;
        currentRow.activity_id = "";
        currentRow.sub_activity_id = "";
        currentRow.custom_sub_activity = "";
        if (normalizeBilledType(value) === "nonbillable" || isLeaveBilledType(value)) {
          currentRow.client_id = "";
          currentRow.business_name = "";
          setClientInputModeByRow((prevModes) => {
            const next = [...prevModes];
            next[rowIndex] = false;
            return next;
          });
        }
        // Leave hours are limited to 4 or 8 — clear invalid existing values
        if (isLeaveBilledType(value)) {
          const hours = String(currentRow.hours || "").trim();
          if (
            hours !== "04:00" &&
            hours !== "08:00" &&
            hours !== "4:00" &&
            hours !== "8:00"
          ) {
            currentRow.hours = "";
          } else if (hours === "4:00") {
            currentRow.hours = "04:00";
          } else if (hours === "8:00") {
            currentRow.hours = "08:00";
          }
        }
        newItemDetails[rowIndex] = currentRow;
        const totalHours = calculation(
          currentRow.hours || "00:00",
          rowIndex,
        );
        return {
          ...prev,
          itemDetails: newItemDetails,
          total_hours: { ...prev.total_hours, value: totalHours },
        };
      });
      setSubActivityOptionsByRow((prev) => {
        const next = [...prev];
        next[rowIndex] = [];
        return next;
      });
    } else if (rowIndex || rowIndex === 0) {
      setFieldConfiguration((prev) => {
        const newItemDetails = [...prev.itemDetails];
        newItemDetails[rowIndex][key] = value;
        return {
          ...prev,
          itemDetails: newItemDetails,
        };
      });
    }
  };

  const isNonBillableRow = React.useCallback(
    (row) => normalizeBilledType(row?.billed_type) === "nonbillable",
    [],
  );

  const isLeaveRow = React.useCallback(
    (row) => isLeaveBilledType(row?.billed_type),
    [],
  );

  const isClientBusinessDisabledRow = React.useCallback(
    (row) => isNonBillableRow(row) || isLeaveRow(row),
    [isNonBillableRow, isLeaveRow],
  );

  const defaultFields = useMemo(() => {
    return [
      {
        type: "datepicker",
        validation: [{ name: "required" }],
        changeData: onChange,
        disabled:
          !fieldConfiguration.start_date.value ||
          !fieldConfiguration.end_date.value,
        minDate:
          fieldConfiguration.start_date.minDate ||
          fieldConfiguration.start_date.value,
        maxDate:
          fieldConfiguration.start_date.maxDate ||
          fieldConfiguration.end_date.value,
        shouldDisableDate: (date) =>
          holidayDateSet.has(
            formatDateOnlyYmd(date) || moment(date).format("YYYY-MM-DD"),
          ),
      },
      {
        type: "select",
        // validation: [{ name: "required" }],
        dropdown: billedTypeOptions,
        error: null,
        errmsg: null,
        changeData: onChange,
        disabled: false,
      },
      {
        type: "select",
        // validation: [{ name: "required" }],
        dropdown: clientsData,
        error: null,
        errmsg: null,
        getFieldType: (rowIndex, rows) => {
          return clientInputModeByRow[rowIndex] ? "text" : "select";
        },
        getValidation: (rowIndex, rows) => {
          return clientInputModeByRow[rowIndex] ? [{ name: "emailNew" }] : [];
        },
        getDisabled: (rowIndex, rows) =>
          isClientBusinessDisabledRow(rows[rowIndex]),
        changeData: onChange,
        disabled: false,
      },
      {
        type: "text",
        error: null,
        errmsg: null,
        getDisabled: (rowIndex, rows) =>
          isClientBusinessDisabledRow(rows[rowIndex]),
        changeData: onChange,
        disabled: false,
      },
      {
        type: "select",
        validation: [{ name: "required" }],
        error: null,
        errmsg: null,
        dropdown: activityOptions,
        dependentDropdown: activityDependentDropdown,
        opt_id: "activity_id",
        opt_value: "activity",
        getValidation: (rowIndex, rows) => {
          if (isLeaveRow(rows[rowIndex])) return [];
          return [{ name: "required" }];
        },
        getDisabled: (rowIndex, rows) => isLeaveRow(rows[rowIndex]),
        changeData: async (value, key, rowIndex) => {
          onChange(value, key, rowIndex);
        },
        disabled: false,
      },
      {
        type: "select", // Default type, will be overridden dynamically
        validation: [{ name: "required" }],
        error: null,
        errmsg: null,
        changeData: onChange,
        dependentDropdown: (fieldConfiguration.itemDetails || []).map(
          (_, i) => subActivityOptionsByRow[i] ?? [],
        ),
        disabled: false,
        opt_id: "sub_activity_id",
        opt_value: "sub_activity",
        // Explicitly set the key for this field
        fieldKey: "sub_activity_id",
        // Custom function to conditionally change type based on row data
        getFieldType: (rowIndex, rows) => {
          const item = rows[rowIndex];
          if (isLeaveRow(item)) return "select";
          const subs = subActivityOptionsByRow[rowIndex] ?? [];
          return isSubActivityTextMode(item, subs) ? "text" : "select";
        },
        // Sub-activity is not required for Leave or Non-billable
        getValidation: (rowIndex, rows) => {
          const item = rows[rowIndex];
          if (
            item &&
            (isLeaveRow(item) ||
              normalizeBilledType(item.billed_type) === "nonbillable")
          ) {
            return [];
          }
          return [{ name: "required" }];
        },
        getDisabled: (rowIndex, rows) => isLeaveRow(rows[rowIndex]),
      },
      {
        type: "textarea",
        error: null,
        errmsg: null,
        changeData: onChange,
        disabled: false,
        rows: 3,
      },
      {
        type: "timeselector",
        validation: [{ name: "required" }],
        error: null,
        errmsg: null,
        // Leave billing: only 4 or 8 hours selectable
        getFieldType: (rowIndex, rows) =>
          isLeaveRow(rows[rowIndex]) ? "select" : "timeselector",
        getDropdown: (rowIndex, rows) =>
          isLeaveRow(rows[rowIndex])
            ? [
                { id: "04:00", value: "04:00" },
                { id: "08:00", value: "08:00" },
              ]
            : [],
        changeData: (value, key, rowIndex) => {
          onChange(value, key, rowIndex);
        },
        disabled: false,
      },
      {
        type: "label",
      },
    ];
  }, [
    clientsData,
    activityOptions,
    activityDependentDropdown,
    // fieldConfiguration.start_date.minDate,
    // fieldConfiguration.start_date.maxDate,
    fieldConfiguration.start_date.value,
    fieldConfiguration.end_date.value,
    holidayDateSet,
    fieldConfiguration.itemDetails,
    clientInputModeByRow,
    subActivityOptionsByRow,
    isLeaveRow,
    isClientBusinessDisabledRow,
  ]);

  const onItemAction = (type, actionData, index) => {
    if (actionData.timesheet_item_id) {
      statusMode.current = {
        ...statusMode.current,
        currentDataItemRemoved: [
          ...(statusMode.current.currentDataItemRemoved || []),
          actionData.timesheet_item_id,
        ],
      };
    }
    setSubActivityOptionsByRow((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
    setClientInputModeByRow((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
    setFieldConfiguration((prev) => {
      const newItemDetails = [...prev.itemDetails];
      newItemDetails.splice(index, 1);
      return {
        ...prev,
        itemDetails: newItemDetails,
      };
    });
  };

  const onSubmit = async (statusValue) => {
    try {
      // Process itemDetails - format dates and handle "Others" case
      const validateTable = submitValidateRef.current;
      const hasError = validateTable && validateTable();

      if (hasError) {
        Toaster.error("Please fill up required fields");
        return;
      }

      const billableRowError = (fieldConfiguration.itemDetails || []).some(
        (item) => item?.billed_type === "Billable" && (!item?.client_id || !item?.business_name),
      );
      if (billableRowError) {
        Toaster.error("Client and Business Name are required for Billable items");
        return;
      }

      if (
        Number(statusValue) !== 0 &&
        isTimesheetTotalBelowMinimum(
          fieldConfiguration.total_hours?.value,
          requiredTimesheetHours,
        )
      ) {
        Toaster.error(
          `Total hours must be at least ${requiredTimesheetHours} hours to submit the timesheet`,
        );
        return;
      }

      const itemParams = fieldConfiguration.itemDetails.map((item, rowIndex) => {
        const processedItem = {
          date: item.date ? moment(item.date).format("YYYY-MM-DD") : "",
          billed_type: item.billed_type,
          client_id: item.client_id,
          client_email: "",
          business_name: item.business_name,
          activity_id: item.activity_id,
          sub_activity_id: item.sub_activity_id,
          hours: item.hours,
          description: item.description,
          timesheet_item_id: item.timesheet_item_id,
          timesheet_id:
            item.timesheet_id || statusMode.current?.timesheet_id || null, // Include timesheet_id for new items
          status: item.status || 0,
          custom_sub_activity: item.custom_sub_activity || "",
        };

        if (clientInputModeByRow?.[rowIndex]) {
          processedItem.client_email = String(processedItem.client_id || "").trim();
          processedItem.client_id = null;
        }

        // If there's no selected activity but custom text exists, treat as custom entry
        if (
          (!processedItem.activity_id || String(processedItem.activity_id).trim() === "") &&
          processedItem.custom_sub_activity
        ) {
          processedItem.activity_id = null;
          processedItem.sub_activity_id = null;
        } else {
          // For normal activities, ensure activity_id is a number (not string)
          if (
            typeof processedItem.activity_id === "string" &&
            processedItem.activity_id !== ""
          ) {
            processedItem.activity_id =
              parseInt(processedItem.activity_id, 10) || null;
          }
          // Ensure sub_activity_id is a number for normal activities
          if (
            typeof processedItem.sub_activity_id === "string" &&
            processedItem.sub_activity_id !== "" &&
            !isNaN(processedItem.sub_activity_id)
          ) {
            processedItem.sub_activity_id = parseInt(
              processedItem.sub_activity_id,
              10,
            );
          }
        }

        // Non-billable rows: sub-activity is free text, store it in
        // custom_sub_activity and null out sub_activity_id (FK is integer).
        if (
          normalizeBilledType(processedItem.billed_type) === "nonbillable"
        ) {
          const subVal = processedItem.sub_activity_id;
          if (typeof subVal === "string" && subVal.trim() !== "") {
            processedItem.custom_sub_activity = subVal.trim();
            processedItem.sub_activity_id = null;
          } else if (subVal === "" || subVal === undefined) {
            processedItem.sub_activity_id = null;
          }
        }

        // Leave rows: activity / sub-activity are not required
        if (isLeaveBilledType(processedItem.billed_type)) {
          processedItem.activity_id = null;
          processedItem.sub_activity_id = null;
          processedItem.custom_sub_activity = "";
          processedItem.client_id = null;
          processedItem.business_name = "";
          processedItem.client_email = "";
        }

        return processedItem;
      });

      const payloadData = {
        start_date: fieldConfiguration.start_date.value,
        end_date: fieldConfiguration.end_date.value,
        status: statusValue,
        total_hours: fieldConfiguration.total_hours.value,
        employee_id: localStorage.getItem("user_id"),
        itemDetails: itemParams,
      };

      const response = await TIMESHEET_ACTION.insertUpdateTimesheet({
        params: payloadData,
        insertUpdateFlag: 1,
        timesheet_id: statusMode.current.timesheet_id,
      });

      if (
        response &&
        response.status === 200 &&
        response.data &&
        response.data.status === 1
      ) {
        Toaster.success(
          `Timesheet ${
            statusValue === 0 ? "saved" : "updated and submitted"
          } successfully`,
        );
        handleClose();
        dispatch(TIMESHEET_ACTION.getAllTimesheets());
      } else if (
        response &&
        response.status === 200 &&
        response.data &&
        response.data.status === 0
      ) {
        Toaster.warning(response.data.message || "Operation failed");
      } else {
        Toaster.error("Error occurred while updating the timesheet");
      }
    } catch (err) {
      console.error("Error in onSubmit:", err);
      Toaster.error(err.message || "Error updating timesheet");
    }
  };

  const effectiveUpdateStatus = () => {
    // Preserve original status: if it was saved/draft (0), update should remain saved
    // unless user explicitly chooses "Submit".
    const prevStatus = Number(statusMode.current?.status);
    return prevStatus === 0 ? 0 : 1;
  };

  useEffect(() => {
    if (!fieldConfiguration?.itemDetails?.length) {
      setFieldConfiguration((prev) => ({
        ...prev,
        total_hours: { ...prev.total_hours, value: "00:00" },
      }));
      return;
    }

    let totalMinutes = 0;

    fieldConfiguration.itemDetails.forEach((item) => {
      if (item.hours && item.hours.includes(":")) {
        const [hh, mm] = item.hours.split(":").map(Number);
        totalMinutes += hh * 60 + mm;
      }
    });

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const formatted =
      String(hours).padStart(2, "0") + ":" + String(minutes).padStart(2, "0");

    setFieldConfiguration((prev) => ({
      ...prev,
      total_hours: { ...prev.total_hours, value: formatted },
    }));
  }, [fieldConfiguration.itemDetails]);

  return (
    <Modal
      open={open}
      title={null}
      handleClose={handleClose}
      maxWidth
      className="edit-timesheet-modal"
    >
      <div className="edit-timesheet-wrapper">
        <div className="edit-timesheet-header">
          <h2 className="edit-timesheet-title">Edit Timesheet</h2>
          <IconButton
            className="edit-timesheet-close-btn"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className="timesheet-page-wrapper">
          {/* Summary Card */}
          <div className="timesheet-summary-card panel">
            <div className="timesheet-summary-content">
              <div className="timesheet-date-field flex" style={{ gap: 16 }}>
                <Labelbox
                  type="datepicker"
                  labelname="Start Date"
                  value={fieldConfiguration.start_date.value || null}
                  error={fieldConfiguration.start_date.error}
                  errmsg={fieldConfiguration.start_date.errmsg}
                  disabled
                  // changeData={(value) => {
                  //   if (!value) return;

                  //   const { startDate, endDate } =
                  //     getWeekRangeSundayToFriday(value);

                  //   setFieldConfiguration((prev) => ({
                  //     ...prev,

                  //     // ✅ Update header dates
                  //     start_date: {
                  //       ...prev.start_date,
                  //       value: startDate,
                  //       minDate: startDate,
                  //       maxDate: endDate,
                  //       error: null,
                  //       errmsg: null,
                  //     },
                  //     end_date: {
                  //       value: endDate,
                  //     },

                  //     // 🔥 RESET ALL TABLE DATES
                  //     itemDetails: prev.itemDetails.map((item) => ({
                  //       ...item,
                  //       date: "", // clear date
                  //     })),
                  //   }));
                  // }}
                />

                <Labelbox
                  type="datepicker"
                  labelname="End Date"
                  value={fieldConfiguration.end_date.value || null}
                  disabled
                />
              </div>
              <div className="timesheet-total-hours">
                <div className="timesheet-total-hours-label">Total Hours:</div>
                <div className="timesheet-total-hours-value">
                  {fieldConfiguration.total_hours.value}
                </div>
              </div>
            </div>
          </div>

          {/* Rejected Comments Section (if exists) */}
          {fieldConfiguration.rejected_comments.value && (
            <div className="rejected-comments-section">
              <h3 className="rejected-comments-title">Rejected Comments</h3>
              <div className="rejected-comments-content">
                {fieldConfiguration.rejected_comments.value}
              </div>
            </div>
          )}

          {/* Table Card */}
          <div className="timesheet-table-card panel">
            <div className="timesheet-table-header">
              <div className="timesheet-table-title">Add Items</div>
              <Button
                // iconCls="btn_icon"
                icon={<AddCircleOutlineOutlined />}
                text="Add"
                handleClick={() => {
                  setFieldConfiguration((prev) => ({
                    ...prev,
                    itemDetails: [
                      ...prev.itemDetails,
                      {
                        date: "",
                        billed_type: "",
                        client_id: "",
                        business_name: "",
                        activity_id: "",
                        sub_activity_id: "",
                        description: "",
                        hours: "",
                        custom_sub_activity: "",
                        timesheet_item_id: null,
                        timesheet_id: statusMode.current?.timesheet_id || null,
                        status: 1,
                      },
                    ],
                  }));
                  setClientInputModeByRow((prev) => [...prev, false]);
                  setSubActivityOptionsByRow((prev) => [...prev, []]);
                }}
                variant="outline"
                className="timesheet-add-row-btn"
              />
            </div>
            <div className="timesheet-table-content">
              <Table
                variant="small"
                title=""
                aligncss="edit-timesheet-table"
                header={[...headers, { id: "9", label: "Action" }]}
                data={fieldConfiguration.itemDetails || []}
                fields={defaultFields}
                hiddenFields={[
                  "client_contract_item_id",
                  "client_invoice_item_id",
                  "status",
                  "timesheet_item_id",
                  "timesheet_id",
                  "custom_sub_activity",
                  "__clientTextMode",
                ]}
                actions={["delete"]}
                onActionClick={onItemAction}
                validateRef={submitValidateRef}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="timesheet-action-buttons">
            <Button
              text="Update"
              large
              handleClick={() => onSubmit(effectiveUpdateStatus())}
              className="timesheet-submit-btn"
            />
            <Button
              text="Submit"
              large
              handleClick={() => onSubmit(1)}
              className="timesheet-submit-btn"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditTimesheetModal;
